// Port of android/.../viewmodels/ChannelFavourites.kt (Apple keeps these rules inline in
// ChannelInfoSheet; the Kotlin object is the one with tests).
//
// Favouriting a conversation (shared/API.md §28) — the rules, away from the panel that draws them.
//
// WHY THERE IS NO LOCAL FAVOURITE FLAG. Mattermost models a favourite as a user PREFERENCE
// (`favorite_channel`), and the server reflects it by moving the channel into the team's
// `favorites` sidebar category. Membership of that category is therefore already the local truth,
// and deriving the toggle from it means the control and the row's position in the sidebar cannot
// disagree.
//
// WHY THE TOGGLE HAS AN IN-FLIGHT STATE ANYWAY. That truth arrives with the category refetch, not
// with the 200, so between the tap and the sidebar moving there is a real gap. The control names
// it rather than pretending the tap did nothing.

/** The preference category on the wire. Not a display string — never localise it. */
export const PREFERENCE_CATEGORY = 'favorite_channel';

/** The sidebar category `type` the server moves a favourited channel into. */
export const CATEGORY_TYPE = 'favorites';

/** What the control is currently saying. */
export type State = 'add' | 'remove' | 'working';

/**
 * Everything in the sidebar can be a favourite, direct and group messages included — that is
 * exactly what the desktop offers. An archived channel cannot: it is leaving the sidebar, not
 * being promoted within it.
 */
export function canFavourite(channelType: string, deleteAt: number): boolean {
  if (deleteAt !== 0) return false;
  return channelType === 'O' || channelType === 'P' || channelType === 'D' || channelType === 'G';
}

export function isFavourite(favouriteChannelIds: ReadonlySet<string>, channelId: string): boolean {
  return favouriteChannelIds.has(channelId);
}

export function state(isFavouriteNow: boolean, isWorking: boolean): State {
  if (isWorking) return 'working';
  return isFavouriteNow ? 'remove' : 'add';
}

/**
 * The team whose sidebar the favourite belongs to. A channel carries its own team, but a direct or
 * group message carries NONE (`team_id` is empty on the wire), and its favourite still has to land
 * in some team's `favorites` category — the one the reader is looking at. Returns null when
 * neither is known, which is the one case where the action must not be offered at all.
 */
export function sidebarTeamId(channelTeamId: string, openTeamId: string | null | undefined): string | null {
  if (channelTeamId.trim() !== '') return channelTeamId;
  return openTeamId && openTeamId.trim() !== '' ? openTeamId : null;
}

/** Namespace form, for `ChannelFavourites.state(...)` call sites. */
export const ChannelFavourites = { PREFERENCE_CATEGORY, CATEGORY_TYPE, canFavourite, isFavourite, state, sidebarTeamId } as const;
